import React, { useEffect } from "react";
import { Link } from 'react-router-dom';

const Package = () => {
  useEffect(() => {
    document.title = "Our Packages | Duckman Auto Detailing";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Choose from our PRESTIGE, MAJESTIC, or ROYALE car detailing packages. Each package offers a comprehensive set of services to meet your needs and budget.';
  }, []);
  const packages = [
    {
      name: "PRESTIGE",
      subtitle: "",
      gradient: "linear-gradient(135deg, #059669, #0d9488)",
      interiorServices: [
        "Full vacuum with our PowerBrush system",
        "Glovebox and storage compartments cleaned",
        "Air-con vents detailed",
        "Fabric seats extraction shampooed",
        "Leather seats deep cleaned",
        "Headliner cleaned",
        "Deep steam clean",
        "Dash UV protected",
        "Interior glass cleaned"
      ],
      exteriorServices: [
        "Vehicle washed using PH neutral shampoo",
        "Wheels, arches, and callipers cleaned",
        "Door shuts, petrol cap, arches cleaned",
        "Door jambs cleaned",
        "Vehicle dried using plush microfibre towels",
        "Wheels glossed",
        "Exterior glass cleaned"
      ],
      additionalSection: "Engine Steam Detailing",
      additionalServices: [
        "Engine rinsed using light pressure",
        "Steam used to soften debris and dirt",
        "All-purpose cleaner applied and worked in with a soft bristle detailing brush",
        "Engine rinsed to lift away dirt",
        "Components carefully dried",
        "Dressing applied to protect and rejuvenate"
      ],
      pricing: {
        saloon: "KES 8,000",
        midSuv: "KES 10,000",
        suv: "KES 11,000"
      }
    },
    {
      name: "MAJESTIC",
      subtitle: "PRESTIGE PACKAGE +",
      gradient: "linear-gradient(135deg, #ea580c, #dc2626)",
      additionalSection: "Paint Correction / Buffing",
      additionalServices: [
        "Paintwork treated with a clay bar",
        "Paint treated with an abrasive cutting compound",
        "Paint polished to a high gloss",
        "IPA used to remove oils from polishing phase",
        "Exterior plastics and door trims dressed",
        "Tyres dressed"
      ],
      pricing: {
        saloon: "KES 13,000",
        midSuv: "KES 15,000",
        suv: "KES 18,000"
      }
    },
    {
      name: "ROYALE",
      subtitle: "MAJESTIC PACKAGE +",
      gradient: "linear-gradient(135deg, #9333ea, #ec4899)",
      additionalSection: "Premium Additional Services",
      additionalServices: [
        "Rim and Brake Dust Cleaning",
        "Watermark Removal",
        "Headlight Restoration"
      ],
      pricing: {
        saloon: "KES 26,000",
        midSuv: "KES 29,000",
        suv: "KES 31,500"
      }
    }
  ];

  const PlusIcon = () => (
    <div 
      className="w-12 h-12 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg"
      style={{background: "linear-gradient(135deg, #6b7280, #4b5563)"}}
    >
      +
    </div>
  );

  return (
    <div 
      className="min-h-screen py-16 px-4"
      style={{background: "linear-gradient(135deg, #111827, #374151, #111827)"}}
    >
      {/* Header */}
      <div className="text-center mb-16">
        <h1 className="text-bold font-extrabold font-head underline decoration-red text-white text-4xl md:text-6xl">
          PACKAGES
        </h1>
        <p style={{color: '#9ca3af'}} className="text-lg mt-6 max-w-2xl mx-auto">
          Premium car detailing services tailored to meet your vehicle's needs
        </p>
      </div>

      {/* Packages Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8">
        {packages.map((pkg, index) => (
          <div
            key={index}
            className="group relative backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 hover:bg-white/15 hover:border-white/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
          >
            {/* Package Name */}
            <div 
              className="rounded-2xl p-4 mb-6 text-center shadow-lg"
              style={{background: pkg.gradient}}
            >
              <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
            </div>

            {/* Subtitle */}
            {pkg.subtitle && (
              <>
                <div 
                  className="rounded-lg px-4 py-2 text-center mb-4 opacity-90"
                  style={{background: pkg.gradient}}
                >
                  <span className="text-white font-semibold text-sm">{pkg.subtitle}</span>
                </div>
                <div className="flex justify-center mb-6">
                  <PlusIcon />
                </div>
              </>
            )}

            {/* Interior & Exterior Inclusions */}
            {(pkg.interiorServices || pkg.exteriorServices) && (
              <div className="space-y-6">
                {pkg.interiorServices && (
                  <div>
                    <h3 style={{color: '#ffffff'}} className="font-semibold text-lg mb-3 border-b border-gray-600 pb-2">
                      Interior Inclusions
                    </h3>
                    <ul className="space-y-2">
                      {pkg.interiorServices.map((service, idx) => (
                        <li key={idx} style={{color: '#d1d5db'}} className="text-sm flex items-start">
                          <span style={{color: '#60a5fa'}} className="mr-2 mt-1">•</span>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {pkg.exteriorServices && (
                  <div>
                    <h3 style={{color: '#ffffff'}} className="font-semibold text-lg mb-3 border-b border-gray-600 pb-2">
                      Exterior Inclusions
                    </h3>
                    <ul className="space-y-2">
                      {pkg.exteriorServices.map((service, idx) => (
                        <li key={idx} style={{color: '#d1d5db'}} className="text-sm flex items-start">
                          <span style={{color: '#60a5fa'}} className="mr-2 mt-1">•</span>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Additional Services */}
            {pkg.additionalSection && (
              <div className="mt-6">
                <h3 style={{color: '#ffffff'}} className="font-semibold text-lg mb-3 border-b border-gray-600 pb-2">
                  {pkg.additionalSection}
                </h3>
                <ul className="space-y-2">
                  {pkg.additionalServices.map((service, idx) => (
                    <li key={idx} style={{color: '#d1d5db'}} className="text-sm flex items-start">
                      <span style={{color: '#34d399'}} className="mr-2 mt-1">•</span>
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing */}
            <div 
              className="mt-6 rounded-2xl p-4 shadow-lg"
              style={{background: pkg.gradient}}
            >
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="space-y-1">
                  <div style={{color: 'rgba(255,255,255,0.9)'}} className="font-medium text-sm">Saloon</div>
                  <div style={{color: 'rgba(255,255,255,0.9)'}} className="font-medium text-sm">Mid-SUV</div>
                  <div style={{color: 'rgba(255,255,255,0.9)'}} className="font-medium text-sm">SUV</div>
                </div>
                <div className="space-y-1">
                  <div style={{color: '#ffffff'}} className="font-bold text-sm">{pkg.pricing.saloon}</div>
                  <div style={{color: '#ffffff'}} className="font-bold text-sm">{pkg.pricing.midSuv}</div>
                  <div style={{color: '#ffffff'}} className="font-bold text-sm">{pkg.pricing.suv}</div>
                </div>
              </div>
            </div>

            {/* Popular Badge */}
            {pkg.name === "PRESTIGE" && (
              <div 
                className="absolute -top-3 -right-3 text-black font-bold text-xs px-3 py-1 rounded-full shadow-lg"
                style={{background: "linear-gradient(135deg, #fbbf24, #f97316)"}}
              >
                POPULAR
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="text-center mt-16">
        <Link to='/booking'>
          <button 
            className="text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            style={{background: "linear-gradient(135deg, #2563eb, #9333ea)"}}
          >
            Book Your Package Today
          </button>
        </Link>
      </div>
    </div>
  );
};

export default Package;
